import { useState, useMemo, useCallback } from "react";
import { Bell, Send, ArrowLeft, BookOpen } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import {
  useNotificationTemplates,
  useTriggerContracts,
  useSaveNotificationTemplate,
  useDeactivateNotificationTemplate,
} from "../hooks/useNotifications";
import { TemplateList } from "./TemplateList";
import { TemplateEditor } from "./TemplateEditor";
import { TriggerContractsTable } from "./TriggerContractsTable";
import { BroadcastForm } from "./BroadcastForm";
import type { NotificationTemplate, TriggerContract, NotificationView, SaveNotificationTemplateInput } from "../types";

export function NotificationsPage() {
  const { t } = useLocale();
  const [view, setView] = useState<NotificationView>("templates");
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);

  // Fetch active and inactive templates separately (backend defaults isActive filter)
  const {
    data: activeData,
    loading: activeLoading,
    refetch: refetchActive,
  } = useNotificationTemplates(true);
  const {
    data: inactiveData,
    loading: inactiveLoading,
    refetch: refetchInactive,
  } = useNotificationTemplates(false);

  const { data: contractsData, loading: contractsLoading } = useTriggerContracts();

  const refetchAll = useCallback(() => {
    refetchActive();
    refetchInactive();
  }, [refetchActive, refetchInactive]);

  const [saveTemplate, { loading: saving }] = useSaveNotificationTemplate();
  const [deactivateTemplate] = useDeactivateNotificationTemplate();

  const loading = activeLoading || inactiveLoading;
  const templates: NotificationTemplate[] = useMemo(() => {
    const active = activeData?.notificationTemplates ?? [];
    const inactive = inactiveData?.notificationTemplates ?? [];
    return [...active, ...inactive];
  }, [activeData, inactiveData]);

  const contracts: TriggerContract[] = contractsData?.notificationTriggerContracts ?? [];

  const getContractForTemplate = useCallback(
    (templateName: string): TriggerContract | null => {
      return contracts.find((c) => c.templateName === templateName) ?? null;
    },
    [contracts],
  );

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleEdit = useCallback((template: NotificationTemplate) => {
    setEditingTemplate(template);
    setView("edit");
  }, []);

  const handleDeactivate = useCallback(
    (name: string, locale: string) => {
      if (!window.confirm(t("notifications.confirm.deactivate", { name }))) return;
      deactivateTemplate({ variables: { name, locale } }).then(() => refetchAll());
    },
    [t, deactivateTemplate, refetchAll],
  );

  const handleReactivate = useCallback(
    (template: NotificationTemplate) => {
      if (!window.confirm(t("notifications.confirm.reactivate", { name: template.name }))) return;
      saveTemplate({
        variables: {
          input: {
            name: template.name,
            locale: template.locale,
            version: template.version,
            title: template.title,
            body: template.body,
            channelId: template.channelId,
            priority: template.priority,
            category: template.category,
          },
        },
      }).then(() => refetchAll());
    },
    [t, saveTemplate, refetchAll],
  );

  const handleSave = useCallback(
    async (input: SaveNotificationTemplateInput) => {
      try {
        await saveTemplate({ variables: { input } });
        refetchAll();
        setView("templates");
        setEditingTemplate(null);
      } catch (err) {
        console.error("[NotificationsPage] saveTemplate error:", err);
      }
    },
    [saveTemplate, refetchAll],
  );

  // ─── Broadcast View ──────────────────────────────────────────────────
  if (view === "broadcast") {
    return <BroadcastForm onBack={() => setView("templates")} />;
  }

  // ─── Contracts View ──────────────────────────────────────────────────
  if (view === "contracts") {
    return (
      <div>
        <button
          type="button"
          onClick={() => setView("templates")}
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("notifications.common.backToTemplates")}
        </button>
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">{t("notifications.triggerContracts.title")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("notifications.triggerContracts.description")}
          </p>
        </div>
        <TriggerContractsTable contracts={contracts} loading={contractsLoading} />
      </div>
    );
  }

  // ─── Editor View ─────────────────────────────────────────────────────
  if (view === "edit" && editingTemplate) {
    return (
      <TemplateEditor
        template={editingTemplate}
        contract={getContractForTemplate(editingTemplate.name)}
        onSave={handleSave}
        onCancel={() => {
          setView("templates");
          setEditingTemplate(null);
        }}
        saving={saving}
      />
    );
  }

  // ─── Templates List View (default) ──────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <Bell className="h-7 w-7" />
            {t("notifications.page.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("notifications.page.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setView("contracts")}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-border bg-background px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            <BookOpen className="h-4 w-4" />
            {t("notifications.triggerContracts.title")}
          </button>
          <button
            type="button"
            onClick={() => setView("broadcast")}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Send className="h-4 w-4" />
            {t("notifications.page.sendNotification")}
          </button>
        </div>
      </div>

      {/* Template list */}
      <TemplateList
        templates={templates}
        loading={loading}
        onEdit={handleEdit}
        onDeactivate={handleDeactivate}
        onReactivate={handleReactivate}
      />
    </div>
  );
}
